<template>
  <div class="guide-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <!-- ===== Hero ===== -->
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Facturas / Guía</p>
            <h1 class="list-title">Cómo crear una factura</h1>
            <p class="list-subtitle">
              Guía paso a paso para registrar una factura y, si aplica,
              agruparla con otras en un mismo consolidado.
            </p>
          </div>
        </v-flex>

        <!-- ===== Pasos ===== -->
        <v-flex md8 xs12>
          <!-- Paso 1 -->
          <v-card class="guide-card">
            <div class="guide-step">
              <span class="guide-step__num">1</span>
              <div class="guide-step__body">
                <h2 class="guide-step__title">Abrir “Nueva Factura”</h2>
                <p class="guide-step__text">
                  En el menú <strong>Facturas → Nuevo</strong>. Se abre el
                  formulario con dos secciones: <em>Datos de la factura</em> y
                  <em>Productos de la factura</em>.
                </p>
              </div>
            </div>
          </v-card>

          <!-- Paso 2 -->
          <v-card class="guide-card">
            <div class="guide-step">
              <span class="guide-step__num">2</span>
              <div class="guide-step__body">
                <h2 class="guide-step__title">
                  Llenar los datos de la factura
                </h2>
                <p class="guide-step__text">
                  Completa los campos en este orden. Los marcados con
                  <span class="req">*</span> son obligatorios.
                </p>
                <ul class="guide-fields">
                  <li>
                    <span class="req">*</span> <strong>Distribuidor</strong> y
                    <strong>Laboratorio</strong> — se eligen de la lista.
                  </li>
                  <li>
                    <span class="req">*</span> <strong># Factura</strong> —
                    número de la factura.
                  </li>
                  <li><strong>CIF (USD)</strong> — valor CIF.</li>
                  <li><strong>Peso Bruto (kg)</strong>.</li>
                  <li>
                    <span class="req">*</span> <strong>Bulto</strong> — cantidad
                    de bultos.
                  </li>
                  <li>
                    <strong>Documento de Transporte</strong> — BL, AWB, CMR
                    (terrestre) o Lista de Empaque.
                  </li>
                  <li>
                    <strong>Visible para</strong> y
                    <strong>Pendiente Documento</strong>.
                  </li>
                  <li>
                    <strong>Observaciones</strong> — motivo si el trámite aún no
                    ha iniciado (queda registrado para el KPI).
                  </li>
                </ul>
              </div>
            </div>
          </v-card>

          <!-- Paso 3 -->
          <v-card class="guide-card">
            <div class="guide-step">
              <span class="guide-step__num">3</span>
              <div class="guide-step__body">
                <h2 class="guide-step__title">Agregar los productos</h2>
                <p class="guide-step__text">
                  Pulsa <strong>“Agregar producto”</strong> y busca cada uno por
                  su <strong>Reg. Sanitario</strong>. Al elegirlo se completan
                  solos el producto, la presentación, el laboratorio y el órgano
                  anuente.
                </p>
                <div class="guide-tip">
                  <v-icon small class="guide-tip__icon">info</v-icon>
                  Para ver los registros, primero debes haber seleccionado el
                  <strong>laboratorio</strong>. Si un producto está vencido o
                  <strong>próximo a vencer</strong> (≤ 6 meses), aparece una
                  etiqueta de aviso.
                </div>
              </div>
            </div>
          </v-card>

          <!-- Paso 4 -->
          <v-card class="guide-card">
            <div class="guide-step">
              <span class="guide-step__num">4</span>
              <div class="guide-step__body">
                <h2 class="guide-step__title">
                  (Opcional) Varias facturas en un consolidado
                </h2>
                <p class="guide-step__text">
                  Si varias facturas van juntas, pulsa
                  <strong>“Agregar al consolidado”</strong>: la factura pasa a
                  la lista de arriba y el formulario se limpia para la siguiente
                  (conserva Distribuidor y Laboratorio). Repite por cada
                  factura.
                </p>
                <div class="guide-tip">
                  <v-icon small class="guide-tip__icon">layers</v-icon>
                  Al enviar 2 o más, el sistema genera un consecutivo
                  <strong>CONS-AAAAMMDD-NNNN</strong> automático que las amarra
                  en un solo trámite del listado. Además, cada factura recibe al
                  guardarse su propio consecutivo
                  <strong>IND-AAAAMMDD-NNNN</strong>.
                </div>
              </div>
            </div>
          </v-card>

          <!-- Paso 5 -->
          <v-card class="guide-card">
            <div class="guide-step">
              <span class="guide-step__num">5</span>
              <div class="guide-step__body">
                <h2 class="guide-step__title">Enviar</h2>
                <p class="guide-step__text">
                  Pulsa <strong>“Enviar factura”</strong> (o
                  <strong>“Enviar consolidado (N)”</strong> si acumulaste
                  varias). Al terminar vuelves al
                  <strong>Listado de Facturas</strong>, donde verás la factura o
                  el trámite consolidado.
                </p>
                <router-link to="/facturas/nuevo" class="guide-cta">
                  <v-icon small class="guide-cta__icon">add</v-icon>
                  Ir a Nueva Factura
                </router-link>
              </div>
            </div>
          </v-card>
        </v-flex>

        <!-- ===== Panel lateral ===== -->
        <v-flex md4 xs12>
          <v-card class="guide-card guide-aside">
            <h3 class="guide-aside__title">
              <v-icon small class="guide-aside__icon">bolt</v-icon>
              Datos que se llenan solos
            </h3>
            <ul class="guide-aside__list">
              <li><strong>Fecha de recepción</strong> — automática.</li>
              <li>
                <strong># Control</strong> — se asigna solo (toma el # Factura);
                el número real de F&D se pone después.
              </li>
              <li><strong># Pedido</strong> — ya no se pide en pantalla.</li>
              <li>
                <strong>Consecutivo individual (IND-AAAAMMDD-NNNN)</strong>:
                cada factura recibe el suyo al guardarse.
              </li>
              <li>
                <strong>Consolidado (CONS-AAAAMMDD-NNNN)</strong>: consecutivo
                que se genera al consolidar.
              </li>
            </ul>
          </v-card>

          <v-card class="guide-card guide-aside">
            <h3 class="guide-aside__title">
              <v-icon small class="guide-aside__icon">lock</v-icon>
              Reglas importantes
            </h3>
            <ul class="guide-aside__list">
              <li>
                Una factura <strong>consolidada</strong> o
                <strong>aprobada</strong> solo permite editar
                <strong>Peso Bruto</strong> y <strong>Observaciones</strong>.
              </li>
              <li>
                Una factura <strong>consolidada no se puede eliminar</strong>;
                primero hay que <strong>Des-consolidar</strong> o
                <strong>Quitar del consolidado</strong>.
              </li>
              <li>
                Desde el listado puedes <strong>Agregar</strong> o
                <strong>Quitar</strong> facturas de un consolidado existente.
              </li>
            </ul>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'ManualFacturas',
}
</script>

<style scoped>
.guide-page {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

/* === Hero === */
.list-hero {
  padding: 4px 0 8px;
}
.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}
.list-title {
  margin: 4px 0;
  font-size: 32px;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 700;
}
.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
  max-width: 760px;
}

/* === Card === */
.guide-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: #ffffff;
  margin-bottom: 16px;
  padding: 18px 20px;
}

/* === Paso === */
.guide-step {
  display: flex;
  gap: 14px;
}
.guide-step__num {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #254f87;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.guide-step__title {
  margin: 4px 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}
.guide-step__text {
  margin: 0;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
}

.guide-fields {
  margin: 10px 0 0;
  padding-left: 18px;
}
.guide-fields li {
  font-size: 13.5px;
  color: #475569;
  line-height: 1.7;
}
.req {
  color: #e5252a;
  font-weight: 700;
}

.guide-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #f0f7ff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
}
.guide-tip__icon {
  color: #254f87 !important;
  margin-top: 1px;
}

.guide-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  background: #254f87;
  color: #ffffff !important;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.18s ease;
}
.guide-cta:hover {
  background: #1e4272;
  transform: translateY(-1px);
}
.guide-cta__icon {
  color: #ffffff !important;
}

/* === Panel lateral === */
.guide-aside__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.guide-aside__icon {
  color: #254f87 !important;
}
.guide-aside__list {
  margin: 0;
  padding-left: 18px;
}
.guide-aside__list li {
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 4px;
}

@media (max-width: 959px) {
  .list-title {
    font-size: 26px;
  }
}
</style>
